import homeIcon from "@/assets/img/menu/home.png";
import courseIcon from "@/assets/img/menu/course.png";
import studentIcon from "@/assets/img/menu/student.png";
import questionIcon from "@/assets/img/menu/question.png";
import messageIcon from "@/assets/img/menu/message.png";
import systemIcon from "@/assets/img/menu/system.png";
import router from "@/router";
import { getMenuByRole } from "@/router/menu";
import {
  loadDynamicRoutes,
  fetchRoutersData
} from "@/utils/dynamicRoutes";
import {
  mapToAppPath,
  normalizePath,
  resolveFullPath
} from "@/router/routeMap";
import settings from "@/settings";
import { TEACHER_STATIC_MENUS } from "@/config/teacherStaticMenus";

const MENU_CACHE_KEY = "dynamicMenus";

function isTeacherApp() {
  return !!(settings && settings.isTeacher);
}

const ICON_MAP = {
  home: homeIcon,
  course: courseIcon,
  cources: courseIcon,
  student: studentIcon,
  user: studentIcon,
  member: studentIcon,
  question: questionIcon,
  enter: questionIcon,
  message: messageIcon,
  system: systemIcon,
  dict: systemIcon,
  role: systemIcon,
  menu: systemIcon,
  default: homeIcon
};

const ICON_MAP_KEYS = Object.keys(ICON_MAP);

function resolveMenuIcon(iconName) {
  if (!iconName || iconName === "#") return ICON_MAP.default;
  const lower = String(iconName).toLowerCase();
  const matched = ICON_MAP_KEYS.find(key => lower.includes(key));
  return ICON_MAP[matched] || ICON_MAP.default;
}

function buildLeafMenuItem(item, parentPath) {
  const fullPath = mapToAppPath(parentPath);
  const title =
    (item.meta && item.meta.title) || item.name || fullPath || "菜单";
  return {
    path: parentPath,
    meta: {
      title,
      icon: resolveMenuIcon(item.meta && item.meta.icon)
    },
    children: [
      {
        path: "",
        fullPath,
        meta: { title }
      }
    ]
  };
}

function isRoutable(path) {
  const normalized = mapToAppPath(normalizePath(path));
  if (!normalized || normalized === "/login") return false;
  const { route } = router.resolve(normalized);
  return route.matched.length > 0;
}

function flattenRouterList(routers) {
  const result = [];

  (routers || []).forEach(item => {
    if (!item || item.hidden) return;

    const path = normalizePath(item.path);
    if (path === "/" && item.children && item.children.length) {
      item.children.forEach(child => {
        if (!child.hidden) {
          result.push({
            ...child,
            path: normalizePath(child.path),
            children: child.children || []
          });
        }
      });
      return;
    }

    result.push({
      ...item,
      path,
      children: item.children || []
    });
  });

  return result;
}

function transformRouters(routers) {
  return flattenRouterList(routers)
    .map(item => {
      const parentPath = normalizePath(item.path);
      const childMenus = (item.children || [])
        .filter(child => child && !child.hidden)
        .map(child => {
          const fullPath = mapToAppPath(
            resolveFullPath(parentPath, child.path)
          );
          const parent = normalizePath(parentPath);
          const relativePath = fullPath.startsWith(parent + "/")
            ? fullPath.slice(parent.length + 1)
            : fullPath.replace(/^\//, "");

          return {
            path: relativePath,
            fullPath,
            meta: {
              title: (child.meta && child.meta.title) || child.name || ""
            }
          };
        })
        .filter(child => isRoutable(child.fullPath));

      if (!childMenus.length) {
        if (item.component && isRoutable(parentPath)) {
          return buildLeafMenuItem(item, parentPath);
        }
        return null;
      }

      return {
        path: parentPath,
        meta: {
          title: (item.meta && item.meta.title) || item.name || "",
          icon: resolveMenuIcon(item.meta && item.meta.icon)
        },
        children: childMenus
      };
    })
    .filter(Boolean);
}

function transformStaticMenus(menus) {
  return (menus || []).map(item => {
    const parentPath = normalizePath(item.path);
    const children = (item.children || []).map(child => ({
      ...child,
      fullPath: normalizePath(`${parentPath}/${child.path}`),
      meta: child.meta || {}
    }));

    return {
      ...item,
      path: parentPath,
      meta: {
        ...item.meta,
        icon: item.meta && item.meta.icon
      },
      children
    };
  });
}

export function setCachedMenus(menus) {
  sessionStorage.setItem(MENU_CACHE_KEY, JSON.stringify(menus || []));
}

export function getCachedMenus() {
  try {
    const raw = sessionStorage.getItem(MENU_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

export function clearCachedMenus() {
  sessionStorage.removeItem(MENU_CACHE_KEY);
}

/**
 * 拉取教师端菜单（由静态 getRouters 数据转换，不请求 auth/routers）
 */
export async function fetchUserMenus(options = {}) {
  const { force = false } = options;

  if (!force) {
    const cached = getCachedMenus();
    if (cached && cached.length) {
      return cached;
    }
  } else {
    clearCachedMenus();
  }

  await loadDynamicRoutes();

  try {
    const routersData = await fetchRoutersData();
    if (routersData && routersData.length) {
      const dynamicMenus = transformRouters(routersData);
      if (dynamicMenus.length) {
        setCachedMenus(dynamicMenus);
        return dynamicMenus;
      }
    }
  } catch (e) {
    // 转换失败时走静态菜单兜底
  }

  const staticMenus = transformStaticMenus(TEACHER_STATIC_MENUS || []);
  setCachedMenus(staticMenus);
  return staticMenus;
}

export function buildMenuTree(list, parentId = 0) {
  return (list || [])
    .filter(item => Number(item.parentId) === Number(parentId))
    .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
    .map(item => {
      const children = buildMenuTree(list, item.menuId);
      return {
        ...item,
        children: children.length ? children : undefined
      };
    });
}



// WEBPACK FOOTER //
// ./src/utils/dynamicMenu.js